// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';

import style from './PaymentBox.scss';
import commonStyle from '../../PaymentBoxViewCommon.scss';
import icons from '../../../../../assets/styles/icons.scss';

import type { Invoice, InvoiceItem } from '../../../../../entities';

type Props = {
  invoice: Invoice;
};

type State = {
  open: boolean;
};

const messages = defineMessages({
  order: {
    id: 'payments.paymentBox.order',
  },
  transaction: {
    id: 'payments.paymentBox.transaction',
  },
  state: {
    id: 'payments.paymentBox.state.title',
  },
  pending: {
    id: 'payments.paymentBox.state.pending',
  },
  received: {
    id: 'payments.paymentBox.state.received',
  },
  refundPending: {
    id: 'payments.paymentBox.state.refundPending',
  },
});

export default class PaymentBox extends Component<Props, State> {
  state = {
    open: false,
  };

  handleClickHeader() {
    this.setState((prevState: State) => {
      return { open: !prevState.open }
    });
  }

  getPaymentState = (paymentState: string) => {
    switch (paymentState) {
      case 'PENDING':
        return this.context.intl.formatMessage(messages.pending);
      case 'RECEIVED':
        return this.context.intl.formatMessage(messages.received);
      case 'REFUND_PENDING':
        return this.context.intl.formatMessage(messages.refundPending);
    }
  };

  shouldShowDeduction = (detailDeduction: string): boolean => !!parseFloat(detailDeduction);

  render() {
    const { invoice } = this.props;
    const { open } = this.state;

    const detailItems = invoice && invoice.items.map((item: InvoiceItem, n: number) => (
      <tr key={n}>
        <td className={classNames(style.amount, commonStyle.alignRight)}>
          {item.amount > 9 ? item.amount : `0${item.amount}`}
        </td>
        <td className={commonStyle.name}>
          {item.name}
        </td>
        <td className={commonStyle.price}>
          $ {item.price}
        </td>
      </tr>));

    const refundItems = invoice && invoice.itemCancellations.map((refund: InvoiceItem, n: number) => (
      <tr key={n}>
        <FormattedMessage id="payments.paymentBox.product">{
          txt => <td>{txt} {refund.name}</td>
        }</FormattedMessage>
        <td className={commonStyle.alignRight}>
          $ {refund.price}
        </td>
      </tr>
    ));

    return (
      <article className={commonStyle.paymentWrap}>
        {invoice &&
        (<Fragment>
            <button className={classNames(commonStyle.paymentHeader, { [commonStyle.open]: open })}
                    onClick={this.handleClickHeader.bind(this)}>
              <div className={commonStyle.title}>
                <h2>{`${this.context.intl.formatMessage(messages.order)} ${invoice.orderId}`}</h2>
                <p>{this.context.intl.formatMessage(messages.state)}: {this.getPaymentState(invoice.state)}</p>
              </div>
              <i className={!open ? icons.iconChevronDownLight : icons.iconChevronUpLight}/>
            </button>
            <section className={classNames(commonStyle.box, { [commonStyle.open]: open })}>
              <section className={commonStyle.tableWrap}>
                <table>
                  <tbody className={style.made}>
                  {detailItems}
                  </tbody>
                </table>
                <table>
                  <tbody className={style.made}>
                  <tr>
                    <FormattedMessage id="checkout.summary.subtotal">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice ? invoice.detail.subtotal : '0.00'}</td>
                  </tr>
                  {this.shouldShowDeduction(invoice.detail.subtotalDeduction) &&
                  <tr className={commonStyle.deduction}>
                    <FormattedMessage id={invoice.hasPaidRefunds ? 'checkout.summary.subtotal.deduction'
                      : 'checkout.summary.subtotal.deduction.pending'}>{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.subtotalDeduction}</td>
                  </tr>}
                  </tbody>
                </table>
                {!!refundItems && !!refundItems.length && <table>
                  <tbody className={style.made}>
                  <tr className={style.refund}>
                    <FormattedMessage id="payments.paymentBox.refund">{
                      txt => <td>{txt}</td>
                    }</FormattedMessage>
                  </tr>
                  {refundItems}
                  </tbody>
                </table>}
                <table>
                  <tbody className={style.made}>
                  <tr className={style.qempoServices}>
                    <FormattedMessage id="checkout.summary.taxes">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.taxes}</td>
                  </tr>
                  {this.shouldShowDeduction(invoice.detail.taxesDeduction) &&
                  <tr className={commonStyle.deduction}>
                    <FormattedMessage
                      id={invoice.hasPaidRefunds ? 'checkout.summary.taxes.deduction'
                        : 'checkout.summary.taxes.deduction.pending'}>{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.taxesDeduction}</td>
                  </tr>}
                  <tr className={style.estimatedTaxes}>
                    <FormattedMessage id="checkout.summary.shipping">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.shipping}</td>
                  </tr>
                  {this.shouldShowDeduction(invoice.detail.shippingDeduction) &&
                  <tr className={commonStyle.deduction}>
                    <FormattedMessage
                      id={invoice.hasPaidRefunds ? 'checkout.summary.shipping.deduction'
                        : 'checkout.summary.shipping.deduction.pending'}>{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.shippingDeduction}</td>
                  </tr>}
                  <tr className={style.estimatedTaxes}>
                    <FormattedMessage id="checkout.summary.importationFee">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.importationFee}</td>
                  </tr>
                  {this.shouldShowDeduction(invoice.detail.importationFeeDeduction) &&
                  <tr className={commonStyle.deduction}>
                    <FormattedMessage
                      id={invoice.hasPaidRefunds ? 'checkout.summary.importationFee.deduction'
                        : 'checkout.summary.importationFee.deduction.pending'}>{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.importationFeeDeduction}</td>
                  </tr>}
                  {!!invoice.detail.customsDuties && <tr className={style.estimatedTaxes}>
                    <FormattedMessage id="checkout.summary.customsDuties">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.customsDuties}</td>
                  </tr>}
                  {this.shouldShowDeduction(invoice.detail.customsDutiesDeduction) &&
                  !!invoice.detail.customsDuties &&
                  <tr className={commonStyle.deduction}>
                    <FormattedMessage
                      id={invoice.hasPaidRefunds ? 'checkout.summary.customsDuties.deduction'
                        : 'checkout.summary.customsDuties.deduction.pending'}>{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.customsDutiesDeduction}</td>
                  </tr>}
                  <tr className={style.estimatedTaxes}>
                    <FormattedMessage id="checkout.summary.qempo">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.qempo}</td>
                  </tr>
                  {this.shouldShowDeduction(invoice.detail.qempoDeduction) &&
                  <tr className={commonStyle.deduction}>
                    <FormattedMessage
                      id={invoice.hasPaidRefunds ? 'checkout.summary.qempo.deduction'
                        : 'checkout.summary.qempo.deduction.pending'}>{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.qempoDeduction}</td>
                  </tr>}
                  <tr className={style.estimatedTaxes}>
                    <FormattedMessage id="checkout.summary.national">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.lastMileShipping}</td>
                  </tr>
                  {this.shouldShowDeduction(invoice.detail.lastMileShippingDeduction) &&
                  <tr className={commonStyle.deduction}>
                    <FormattedMessage
                      id={invoice.hasPaidRefunds ? 'checkout.summary.national.deduction'
                        : 'checkout.summary.national.deduction.pending'}>{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.lastMileShippingDeduction}</td>
                  </tr>}

                  {!!parseFloat(invoice.detail.coupon) && <tr className={style.estimatedTaxes}>
                    <FormattedMessage id="checkout.summary.coupon">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.coupon}</td>
                  </tr>}
                  {this.shouldShowDeduction(invoice.detail.couponDeduction) &&
                  <tr className={commonStyle.deduction}>
                    <FormattedMessage
                      id={invoice.hasPaidRefunds ? 'checkout.summary.coupon.deduction'
                        : 'checkout.summary.coupon.deduction.pending'}>{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.couponDeduction}</td>
                  </tr>}
                  </tbody>
                </table>
                {/*!!payment.refund && <table>
                  <tbody className={style.made}>
                  <tr className={style.refund}>
                    <FormattedMessage id="checkout.summary.total.refund">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {payment.refund}</td>
                  </tr>
                  </tbody>
                </table>*/}
                <table>
                  <tbody>
                  <tr className={classNames(commonStyle.total, commonStyle.noLine)}>
                    <FormattedMessage id="checkout.summary.total.title">{
                      text => <td>{text}</td>
                    }</FormattedMessage>
                    <td className={commonStyle.alignRight}>$ {invoice.detail.total}</td>
                  </tr>
                  </tbody>
                </table>
              </section>
              {/*<section className={commonStyle.download}>
            <FormattedMessage id="payments.paymentBox.download">{
              text => <a href={payment.pdf}
                         className={classNames(buttonStyle.btn, buttonStyle.lightBlueInverse)}
                         download="myPayment"><i className={icons.iconChevronDownLight}/> {text}</a>
            }</FormattedMessage>
          </section>*/}
            </section>
          </Fragment>
        )}
      </article>)
  }
}

PaymentBox.propTypes = {
  invoice: PropTypes.object.isRequired,
};

PaymentBox.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/payments/components/PaymentsMade/PaymentBox/PaymentBox.jsx